import {
  $applyNodeReplacement,
  $getEditor,
  $getState,
  $setState,
  createState,
  TextNode,
  type EditorConfig,
  type LexicalEditor,
  type LexicalNode,
  type StateConfig,
  type StaticNodeConfigRecord,
} from "lexical";
import { CONTENT_CLASSES } from "../../core/content-classes";
import {
  CORE_TOKEN_REGISTRY,
  TOKEN_NODE_TYPE,
  TOKEN_PAYLOAD_KEY,
  tokenLabel,
  type InlineToken,
  type TokenRegistry,
} from "../../core/tokens";

/**
 * The typed payload. Parsing trusts its shape: stored content is validated against the token
 * registry before it reaches the editor, and everything else comes from `$createTokenNode`.
 */
export const tokenState: StateConfig<typeof TOKEN_PAYLOAD_KEY, InlineToken | null> = createState(
  TOKEN_PAYLOAD_KEY,
  {
    parse: (value) => (typeof value === "object" && value !== null ? (value as InlineToken) : null),
  },
);

/** Set by the tokens extension, so a node can find its editor's registry while rendering. */
export const registries: WeakMap<LexicalEditor, TokenRegistry> = new WeakMap();

type TokenNodeConfig = StaticNodeConfigRecord<
  typeof TOKEN_NODE_TYPE,
  {
    readonly extends: typeof TextNode;
    readonly stateConfigs: readonly [
      { readonly flat: true; readonly stateConfig: typeof tokenState },
    ];
  }
>;

/**
 * An atomic, text-like reference. It is a TextNode in token mode, so the caret, selection, copy,
 * and formatting treat it as the text of its label, while Backspace and Delete take it whole and
 * typing next to it never edits it. What it refers to lives in the payload, not in the text.
 */
export class TokenNode extends TextNode {
  override $config(): TokenNodeConfig {
    return this.config(TOKEN_NODE_TYPE, {
      extends: TextNode,
      stateConfigs: [{ flat: true, stateConfig: tokenState }],
    });
  }

  getToken(): InlineToken | null {
    return $getState(this, tokenState);
  }

  override createDOM(config: EditorConfig, editor?: LexicalEditor): HTMLElement {
    const dom = super.createDOM(config, editor);
    dom.classList.add(CONTENT_CLASSES.token);
    this.describe(dom, editor);
    return dom;
  }

  override updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    const replace = super.updateDOM(prevNode, dom, config);
    if (!replace) this.describe(dom);
    return replace;
  }

  private describe(dom: HTMLElement, editor?: LexicalEditor): void {
    const token = this.getToken();
    if (token === null) return;
    // `updateDOM` is not handed the editor, but reconciliation always has an active one.
    const registry = registries.get(editor ?? $getEditor()) ?? CORE_TOKEN_REGISTRY;
    dom.dataset.tokenKind = token.kind;
    dom.dataset.tokenAppearance = registry.appearance(token);
    dom.spellcheck = false;
  }

  override canInsertTextBefore(): boolean {
    return false;
  }

  override canInsertTextAfter(): boolean {
    return false;
  }

  override isTextEntity(): boolean {
    return true;
  }
}

export function $createTokenNode(token: InlineToken): TokenNode {
  const node = $applyNodeReplacement(new TokenNode(tokenLabel(token)));
  $setState(node, tokenState, token);
  return node.setMode("token");
}

export const $isTokenNode = (node: LexicalNode | null | undefined): node is TokenNode =>
  node instanceof TokenNode;
